import { Link, useLocation } from '@tanstack/react-router';
import { useMsal } from '@azure/msal-react';
import {
  Avatar,
  Box,
  Divider,
  Group,
  Loader,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconDatabase,
  IconHistory,
  IconKey,
  IconLayoutDashboard,
  IconLogout,
  IconShieldLock,
  IconUsersGroup,
} from '@tabler/icons-react';

import { useMe } from '../auth/api';
import { visibleReportGroups } from '../raporty/nav';

function initials(name: string | null | undefined): string {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

function UserBlock() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const me = useMe();

  const displayName = me.data?.display_name ?? account?.name ?? 'Użytkownik';
  const displayUpn = me.data?.upn ?? account?.username ?? '';

  return (
    <Stack gap="xs">
      <Group gap="sm" wrap="nowrap">
        <Avatar color="blue" radius="xl">
          {initials(displayName)}
        </Avatar>
        <Box style={{ minWidth: 0, flex: 1 }}>
          {me.isPending ? (
            <Loader size="xs" />
          ) : (
            <>
              <Text size="sm" fw={600} truncate>
                {displayName}
              </Text>
              {displayUpn && (
                <Text size="xs" c="dimmed" truncate>
                  {displayUpn}
                </Text>
              )}
            </>
          )}
        </Box>
      </Group>
      <UnstyledButton
        onClick={() => void instance.logoutRedirect()}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <IconLogout size={16} />
        <Text size="sm">Wyloguj się</Text>
      </UnstyledButton>
    </Stack>
  );
}

export function AppSidebar() {
  const { pathname } = useLocation();
  const me = useMe();

  const isAdmin = me.data?.is_admin ?? false;
  const allowed = new Set(me.data?.allowed_reports ?? []);
  // Spis raportów i ich podział na grupy: `raporty/nav.ts`, wspólny z pulpitem.
  const groups = visibleReportGroups(isAdmin, allowed);

  return (
    <Stack gap={0} h="100%">
      <Box p="md">
        <UserBlock />
      </Box>
      <Divider />
      <ScrollArea style={{ flex: 1 }} p="xs">
        <NavLink
          component={Link}
          to="/"
          label="Pulpit"
          leftSection={<IconLayoutDashboard size={18} />}
          active={pathname === '/'}
        />
        {groups.map(({ title, icon: GroupIcon, reports }) => (
          <NavLink
            key={title}
            label={title}
            leftSection={<GroupIcon size={18} />}
            // Grupy startują zwinięte. Wyjątek: ta, w której jesteś — inaczej
            // wchodząc na raport widzisz menu, które nie pokazuje, gdzie stoisz.
            defaultOpened={reports.some((report) => report.to === pathname)}
            childrenOffset={28}
          >
            {reports.map(({ to, label, icon: ReportIcon }) => (
              <NavLink
                key={to}
                component={Link}
                to={to}
                label={label}
                leftSection={<ReportIcon size={16} />}
                active={pathname === to}
              />
            ))}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            label="Administracja"
            leftSection={<IconShieldLock size={18} />}
            defaultOpened={pathname.startsWith('/administracja')}
            childrenOffset={28}
          >
            <NavLink
              component={Link}
              to="/administracja/uzytkownicy"
              label="Użytkownicy"
              leftSection={<IconUsersGroup size={16} />}
              active={pathname === '/administracja/uzytkownicy'}
            />
            <NavLink
              component={Link}
              to="/administracja/role"
              label="Role"
              leftSection={<IconKey size={16} />}
              active={pathname === '/administracja/role'}
            />
            <NavLink
              component={Link}
              to="/administracja/zrodla-danych"
              label="Źródła danych"
              leftSection={<IconDatabase size={16} />}
              active={pathname === '/administracja/zrodla-danych'}
            />
            <NavLink
              component={Link}
              to="/administracja/audit"
              label="Audit log"
              leftSection={<IconHistory size={16} />}
              active={pathname === '/administracja/audit'}
            />
          </NavLink>
        )}
      </ScrollArea>
    </Stack>
  );
}
